import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { PHOTO_TAG_SUGGESTIONS, FIXED_TAG_SET } from '../../_constants/clientPhotos.constants';
import { clientPhotosActions } from '../../_actions/clientPhotos.actions';
import { effectiveShareConsent } from '../../_services/clientPhotos.service';

const CloseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const TrashIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6M14 11v6" />
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
);

const ShareIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3v12" />
        <polyline points="8 7 12 3 16 7" />
        <path d="M4 13v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6" />
    </svg>
);

const PlusIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

const ChevronLeft = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
);

const ChevronRight = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
    </svg>
);

const sharePhoto = async (photo) => {
    try {
        const res = await fetch(photo.url);
        const blob = await res.blob();
        const file = new File([blob], 'photo.jpg', { type: blob.type || 'image/jpeg' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file] });
            return;
        }
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `photo-${Date.now()}.jpg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
    } catch {
        /* user cancelled or browser blocked — silent */
    }
};

const normaliseTag = (raw) => (raw || '').trim().toLowerCase();

const ClientPhotoLightbox = ({ items, initialIndex, onClose, onDelete, onRetag, onViewBooking, consent, onShareConsent }) => {
    const dispatch = useDispatch();
    const [index, setIndex] = useState(initialIndex);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerDraft, setPickerDraft] = useState('');
    const pickerInputRef = useRef(null);

    // Touch gesture state for swipe-to-dismiss + swipe-to-navigate.
    // Inspired by iOS Photos: horizontal swipe navigates, vertical
    // downward swipe dismisses. Only active on the stage (the image
    // area) so the footer can still scroll and tag chips stay tappable.
    //
    // We duplicate into a ref because React state updates are async
    // and touchend can fire in the same event tick as touchstart, so
    // reading `drag` from state gives stale values — the ref is always
    // current. State is kept only to drive the visual transform during
    // an active drag.
    const [drag, setDrag] = useState(null); // { dx, dy, axis }
    const dragRef = useRef(null);
    const touchStartRef = useRef(null);
    // Set on touchEnd so the synthetic click that follows doesn't
    // double-toggle the chrome-hidden state. Cleared after 400ms, long
    // enough to cover iOS's synthetic click delay.
    const touchJustEndedRef = useRef(false);
    // Desktop mouse drag state (declared up here so the unmount
    // cleanup effect below can reference them before the early return).
    const mouseDraggedRef = useRef(false);
    const mouseMoveHandlerRef = useRef(null);
    const mouseUpHandlerRef = useRef(null);

    // Apple Photos style: tap the image to hide the chrome (header +
    // footer) so only the photo remains. Useful for privacy when the
    // booking note contains sensitive info. Tap again to bring it back.
    const [chromeHidden, setChromeHidden] = useState(false);

    // Local optimistic tag overlay per photo. We keep this in
    // component state so the picker instantly reflects tag changes
    // without waiting for the Redux roundtrip. When the user
    // navigates to a different photo, the overlay for the previous
    // one is stale but harmless — Redux is the source of truth.
    const [localTagOverrides, setLocalTagOverrides] = useState({});

    const salonTags = useSelector((s) => s.clientPhotos?.salonTags || []);
    const contextTagsByService = useSelector((s) => s.clientPhotos?.contextTagsByService || {});

    const current = items[index];

    // When the visible photo changes, eagerly fetch tag suggestions that
    // were learned from other photos booked for the same service. This is
    // the "magic" layer — suggestions reflect what the team actually uses
    // for this kind of work.
    useEffect(() => {
        if (current?.bookingService) {
            dispatch(clientPhotosActions.fetchTagsForService(current.bookingService));
        }
    }, [current?.bookingService, dispatch]);

    const prev = useCallback(() => {
        setIndex((i) => (i > 0 ? i - 1 : i));
        setPickerOpen(false);
    }, []);
    const next = useCallback(() => {
        setIndex((i) => (i < items.length - 1 ? i + 1 : i));
        setPickerOpen(false);
    }, [items.length]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') {
                if (pickerOpen) { setPickerOpen(false); return; }
                onClose();
            }
            if (e.key === 'ArrowLeft' && !pickerOpen) prev();
            if (e.key === 'ArrowRight' && !pickerOpen) next();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, prev, next, pickerOpen]);

    useEffect(() => {
        if (pickerOpen && pickerInputRef.current) pickerInputRef.current.focus();
    }, [pickerOpen]);

    // Clean up any dangling window-level mouse listeners on unmount
    // so a mid-drag unmount doesn't leak handlers that keep firing.
    useEffect(() => () => {
        if (mouseMoveHandlerRef.current) window.removeEventListener('mousemove', mouseMoveHandlerRef.current);
        if (mouseUpHandlerRef.current) window.removeEventListener('mouseup', mouseUpHandlerRef.current);
    }, []);

    // Suggestions are computed as two STABLE lists:
    //   1. Context suggestions — tags used on other photos booked for the
    //      same service as this one. These feel magic because they reflect
    //      exactly what the team already uses for this kind of work.
    //   2. General suggestions — the 4 fixed quick-picks + salon-wide
    //      popular tags, minus any tag already in the context list.
    //
    // The ORDER of each list is frozen while the picker is open — we never
    // filter applied tags out, we just mark them as selected so toggling
    // a tag never causes a layout jump.
    const contextTags = current?.bookingService
        ? contextTagsByService[current.bookingService] || []
        : [];

    const { contextList, generalList } = useMemo(() => {
        const draft = normaliseTag(pickerDraft);
        const contextSeen = new Set();
        const ctx = [];
        contextTags.forEach((row) => {
            const name = normaliseTag(row.name);
            if (!name || contextSeen.has(name)) return;
            contextSeen.add(name);
            ctx.push({ name, count: row.count || 0, fixed: FIXED_TAG_SET.has(name) });
        });

        const generalSeen = new Set(contextSeen);
        const general = [];
        PHOTO_TAG_SUGGESTIONS.forEach((t) => {
            if (generalSeen.has(t)) return;
            general.push({ name: t, count: 0, fixed: true });
            generalSeen.add(t);
        });
        salonTags.forEach((row) => {
            const name = normaliseTag(row.name);
            if (!name || generalSeen.has(name) || FIXED_TAG_SET.has(name)) return;
            general.push({ name, count: row.count || 0, fixed: false });
            generalSeen.add(name);
        });

        if (!draft) return { contextList: ctx, generalList: general };
        return {
            contextList: ctx.filter((s) => s.name.includes(draft)),
            generalList: general.filter((s) => s.name.includes(draft)),
        };
    }, [contextTags, salonTags, pickerDraft]);

    if (!current) return null;

    // Use local override if available (instant feedback), else Redux.
    const currentTags = (current.id && localTagOverrides[current.id]) || current.tags || [];

    const handleDeleteClick = () => {
        if (!confirmingDelete) {
            setConfirmingDelete(true);
            setTimeout(() => setConfirmingDelete(false), 2400);
            return;
        }
        onDelete(current.id);
    };

    const commitTags = (newList) => {
        const deduped = Array.from(new Set(newList.map(normaliseTag).filter(Boolean)));
        // Optimistically update local state so the picker is instantly
        // responsive, then dispatch to Redux for persistence.
        setLocalTagOverrides((prev) => ({ ...prev, [current.id]: deduped }));
        onRetag(current.id, deduped, current.tags || []);
    };

    // Sharing on social media: the salon records that the client said yes,
    // photo by photo (the server keeps who and when), or the client said yes
    // for all their photos (consent.social). The old `social` tag is not
    // consent; it's only hidden from the tag list. docs/SOCIAL_BACKEND.md §2.
    const SOCIAL = 'social';
    const share = effectiveShareConsent(current, consent);
    const shareOk = share !== 'none';
    const toggleShare = () => {
        if (!onShareConsent) return;
        onShareConsent(current.id, shareOk ? 'none' : 'social', current);
    };
    const shareSub = share === 'noFace'
        ? 'They’ve said yes, as long as their face isn’t shown.'
        : shareOk
            ? (current.shareConsentAt ? 'They’ve said yes, so it can be used in posts.' : 'They’ve said yes to all their photos, so it can be used in posts.')
            : 'Switch on only when they’ve said yes.';
    const stripTags = currentTags.filter((t) => normaliseTag(t) !== SOCIAL);

    const addTag = (raw) => {
        const tag = normaliseTag(raw);
        if (!tag) return;
        if (currentTags.map(normaliseTag).includes(tag)) return;
        setPickerDraft('');
        commitTags([...currentTags, tag]);
    };

    const removeTag = (tag) => {
        commitTags(currentTags.filter((t) => normaliseTag(t) !== normaliseTag(tag)));
    };

    const handleInputKey = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (pickerDraft.trim()) addTag(pickerDraft);
        }
        if (e.key === 'Backspace' && !pickerDraft && currentTags.length > 0) {
            removeTag(currentTags[currentTags.length - 1]);
        }
    };

    const draftNormalised = normaliseTag(pickerDraft);
    const draftCanCreate = draftNormalised.length > 0
        && !currentTags.map(normaliseTag).includes(draftNormalised)
        && !contextList.some((s) => s.name === draftNormalised)
        && !generalList.some((s) => s.name === draftNormalised);

    const currentTagSet = new Set(currentTags.map(normaliseTag));
    const toggleTag = (name) => {
        if (currentTagSet.has(name)) {
            removeTag(name);
        } else {
            addTag(name);
        }
    };

    // ── Touch gestures ──────────────────────────────────────────
    const SWIPE_THRESHOLD = 60;
    const AXIS_LOCK_THRESHOLD = 8;

    const handleTouchStart = (e) => {
        // Stop propagation so touch gestures inside the portalled
        // lightbox don't bubble up through React's tree and trigger
        // the tab swipe handler in ClientDetailPanel.
        e.stopPropagation();
        if (e.touches.length !== 1) return;
        const t = e.touches[0];
        touchStartRef.current = { x: t.clientX, y: t.clientY, axis: null };
        const fresh = { dx: 0, dy: 0, axis: null };
        dragRef.current = fresh;
        setDrag(fresh);
    };

    const handleTouchMove = (e) => {
        e.stopPropagation();
        const start = touchStartRef.current;
        if (!start || e.touches.length !== 1) return;
        const t = e.touches[0];
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;

        // Lock axis once movement exceeds a tiny threshold so a diagonal
        // drag doesn't feel wobbly.
        let axis = start.axis;
        if (!axis) {
            if (Math.abs(dx) > AXIS_LOCK_THRESHOLD || Math.abs(dy) > AXIS_LOCK_THRESHOLD) {
                axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
                touchStartRef.current = { ...start, axis };
            }
        }

        if (axis === 'x') {
            const blocked = (dx > 0 && index === 0) || (dx < 0 && index === items.length - 1);
            const next = { dx: blocked ? dx * 0.25 : dx, dy: 0, axis };
            dragRef.current = next;
            setDrag(next);
        } else if (axis === 'y') {
            const next = { dx: 0, dy: dy > 0 ? dy : dy * 0.25, axis };
            dragRef.current = next;
            setDrag(next);
        } else {
            // Still under the axis-lock threshold; keep drag at zero.
            const next = { dx, dy, axis: null };
            dragRef.current = next;
        }
    };

    const handleTouchEnd = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        const d = dragRef.current || { dx: 0, dy: 0, axis: null };
        touchStartRef.current = null;
        dragRef.current = null;

        // Block the synthetic click that iOS fires after touchend
        // (~300ms later) so the click handler doesn't undo our toggle.
        touchJustEndedRef.current = true;
        setTimeout(() => { touchJustEndedRef.current = false; }, 400);

        const isTap = d.axis === null
            && Math.abs(d.dx) < AXIS_LOCK_THRESHOLD
            && Math.abs(d.dy) < AXIS_LOCK_THRESHOLD;

        if (isTap) {
            if (pickerOpen) {
                setPickerOpen(false);
            } else {
                setChromeHidden((v) => !v);
            }
            setDrag(null);
            return;
        }

        if (d.axis === 'x') {
            if (d.dx <= -SWIPE_THRESHOLD && index < items.length - 1) {
                next();
            } else if (d.dx >= SWIPE_THRESHOLD && index > 0) {
                prev();
            }
        } else if (d.axis === 'y') {
            if (d.dy >= SWIPE_THRESHOLD * 1.4) {
                onClose();
                return;
            }
        }
        setDrag(null);
    };

    // Desktop click handler on the stage. On mobile we ignore this so
    // the synthetic click that follows a real touch doesn't double-
    // toggle the chrome-hidden state. On desktop we also ignore the
    // click that follows a drag (click fires on mouseup even if the
    // user dragged the cursor around).
    const handleStageClick = (e) => {
        if (touchJustEndedRef.current) return;
        if (mouseDraggedRef.current) {
            mouseDraggedRef.current = false;
            return;
        }
        if (e.target.closest('.cpl-nav')) return;
        if (pickerOpen) {
            setPickerOpen(false);
            return;
        }
        setChromeHidden((v) => !v);
    };

    // ── Desktop mouse drag ─────────────────────────────────────
    // Parallel to the touch handlers so the swipe-to-close gesture
    // works with a trackpad/mouse too. mousedown starts tracking, we
    // attach window listeners so the drag continues outside the stage,
    // and on mouseup we run the same axis/threshold logic as touch.
    // A flag tells handleStageClick to swallow the subsequent click
    // when the release was a drag, not a tap.
    const handleMouseDown = (e) => {
        if (e.button !== 0) return; // left click only
        if (e.target.closest('.cpl-nav')) return;
        touchStartRef.current = { x: e.clientX, y: e.clientY, axis: null };
        const fresh = { dx: 0, dy: 0, axis: null };
        dragRef.current = fresh;
        setDrag(fresh);

        const onMove = (ev) => {
            const start = touchStartRef.current;
            if (!start) return;
            const dx = ev.clientX - start.x;
            const dy = ev.clientY - start.y;
            let axis = start.axis;
            if (!axis && (Math.abs(dx) > AXIS_LOCK_THRESHOLD || Math.abs(dy) > AXIS_LOCK_THRESHOLD)) {
                axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
                touchStartRef.current = { ...start, axis };
            }
            if (axis === 'x') {
                const blocked = (dx > 0 && index === 0) || (dx < 0 && index === items.length - 1);
                const next = { dx: blocked ? dx * 0.25 : dx, dy: 0, axis };
                dragRef.current = next;
                setDrag(next);
            } else if (axis === 'y') {
                const next = { dx: 0, dy: dy > 0 ? dy : dy * 0.25, axis };
                dragRef.current = next;
                setDrag(next);
            } else {
                dragRef.current = { dx, dy, axis: null };
            }
        };

        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            mouseMoveHandlerRef.current = null;
            mouseUpHandlerRef.current = null;

            const d = dragRef.current || { dx: 0, dy: 0, axis: null };
            touchStartRef.current = null;
            dragRef.current = null;

            const moved = Math.abs(d.dx) >= AXIS_LOCK_THRESHOLD || Math.abs(d.dy) >= AXIS_LOCK_THRESHOLD;
            if (!moved) {
                // No meaningful drag — let the normal click handler fire
                // for tap-to-hide-chrome.
                mouseDraggedRef.current = false;
                setDrag(null);
                return;
            }

            // A drag happened — suppress the click that will follow mouseup.
            mouseDraggedRef.current = true;

            if (d.axis === 'x') {
                if (d.dx <= -SWIPE_THRESHOLD && index < items.length - 1) {
                    next();
                } else if (d.dx >= SWIPE_THRESHOLD && index > 0) {
                    prev();
                }
            } else if (d.axis === 'y' && d.dy >= SWIPE_THRESHOLD * 1.4) {
                onClose();
                return;
            }
            setDrag(null);
        };

        mouseMoveHandlerRef.current = onMove;
        mouseUpHandlerRef.current = onUp;
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    // Compute inline transform for the stage during an active drag.
    const stageStyle = drag && drag.axis
        ? {
            transform: `translate3d(${drag.dx}px, ${drag.dy}px, 0)`,
            transition: 'none',
            // Fade backdrop-like opacity as you drag down.
            opacity: drag.axis === 'y' && drag.dy > 0
                ? Math.max(0.55, 1 - drag.dy / 400)
                : 1,
        }
        : {
            transform: 'translate3d(0, 0, 0)',
            transition: 'transform 260ms cubic-bezier(0.2, 0.85, 0.3, 1.1), opacity 260ms ease',
            opacity: 1,
        };

    const renderSuggestionChip = (s) => {
        const selected = currentTagSet.has(s.name);
        return (
            <button
                key={s.name}
                type="button"
                className={`cpl-chip ${s.fixed ? `cpl-chip--${s.name}` : 'cpl-chip--custom'} cpl-chip--suggest ${selected ? 'cpl-chip--selected' : ''}`}
                onClick={() => toggleTag(s.name)}
            >
                {s.name}
                {s.count > 0 && !selected && <span className="cpl-chip-count">{s.count}</span>}
                {selected && (
                    <svg className="cpl-chip-check" width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="2.5 7.5 5.5 10.5 11.5 3.5" />
                    </svg>
                )}
            </button>
        );
    };

    const renderTagChip = (tag, removable) => {
        const fixed = FIXED_TAG_SET.has(tag);
        return (
            <span key={tag} className={`cpl-chip ${fixed ? `cpl-chip--${tag}` : 'cpl-chip--custom'} ${removable ? 'cpl-chip--removable' : ''}`}>
                {tag}
                {removable && (
                    <button type="button" className="cpl-chip-remove" onClick={() => removeTag(tag)} aria-label={`Remove ${tag}`}>
                        <svg width="8" height="8" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                            <line x1="3.5" y1="3.5" x2="10.5" y2="10.5" />
                            <line x1="10.5" y1="3.5" x2="3.5" y2="10.5" />
                        </svg>
                    </button>
                )}
            </span>
        );
    };

    const handleBackdropClick = () => {
        if (pickerOpen) {
            setPickerOpen(false);
            return;
        }
        onClose();
    };

    const handleFrameClick = () => {
        // When the picker is open, a click anywhere inside the frame that
        // isn't the picker itself should just dismiss the picker (it's
        // stopPropagation'd on the picker div). Keep the lightbox open.
        if (pickerOpen) setPickerOpen(false);
    };

    const stopTouchPropagation = (e) => e.stopPropagation();

    const node = (
        <div
            className={`cpl-root ${chromeHidden ? 'cpl-root--chrome-hidden' : ''}`}
            onClick={handleBackdropClick}
            onTouchStart={stopTouchPropagation}
            onTouchMove={stopTouchPropagation}
            onTouchEnd={stopTouchPropagation}
        >
            <div className="cpl-frame" onClick={(e) => { e.stopPropagation(); handleFrameClick(); }}>
                <header className="cpl-header">
                    <div className="cpl-header-left">
                        <div className="cpl-tag-strip">
                            {stripTags.length === 0 ? (
                                <button
                                    type="button"
                                    className="cpl-chip cpl-chip--placeholder"
                                    onClick={() => setPickerOpen((v) => !v)}
                                >
                                    <PlusIcon />
                                    <span>Add tags</span>
                                </button>
                            ) : (
                                <>
                                    {stripTags.slice(0, 3).map((t) => (
                                        <span
                                            key={t}
                                            className={`cpl-chip ${FIXED_TAG_SET.has(t) ? `cpl-chip--${t}` : 'cpl-chip--custom'}`}
                                        >
                                            {t}
                                        </span>
                                    ))}
                                    {stripTags.length > 3 && (
                                        <span className="cpl-chip cpl-chip--more">+{stripTags.length - 3}</span>
                                    )}
                                    <button
                                        type="button"
                                        className="cpl-tag-edit"
                                        onClick={() => setPickerOpen((v) => !v)}
                                        aria-label="Edit tags"
                                    >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 20h9" />
                                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                                        </svg>
                                    </button>
                                </>
                            )}
                        </div>
                        {pickerOpen && (
                            <div className="cpl-picker" onClick={(e) => e.stopPropagation()}>
                                <div className="cpl-picker-input-row">
                                    {currentTags.map((t) => renderTagChip(t, true))}
                                    <input
                                        ref={pickerInputRef}
                                        type="text"
                                        value={pickerDraft}
                                        onChange={(e) => setPickerDraft(e.target.value)}
                                        onKeyDown={handleInputKey}
                                        placeholder={currentTags.length ? 'Add another…' : 'Add a tag, then Enter'}
                                        className="cpl-picker-input"
                                    />
                                </div>
                                {draftCanCreate && (
                                    <button
                                        type="button"
                                        className="cpl-picker-create"
                                        onClick={() => addTag(pickerDraft)}
                                    >
                                        <PlusIcon />
                                        <span>Create “{normaliseTag(pickerDraft)}”</span>
                                    </button>
                                )}
                                {contextList.length > 0 && (
                                    <div className="cpl-picker-section">
                                        <p className="cpl-picker-label">
                                            <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                                                <path d="M8 1l1.9 4.3L14 6l-3.2 2.9L11.7 13 8 10.8 4.3 13l.9-4.1L2 6l4.1-.7L8 1z" fill="currentColor" />
                                            </svg>
                                            Used on {current.bookingService ? current.bookingService.toLowerCase() : 'similar'} bookings
                                        </p>
                                        <div className="cpl-picker-suggestions">
                                            {contextList.map(renderSuggestionChip)}
                                        </div>
                                    </div>
                                )}
                                {generalList.length > 0 && (
                                    <div className="cpl-picker-section">
                                        {contextList.length > 0 && (
                                            <p className="cpl-picker-label">All tags</p>
                                        )}
                                        <div className="cpl-picker-suggestions">
                                            {generalList.map(renderSuggestionChip)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="cpl-header-right">
                        <button type="button" className="cpl-icon-btn" onClick={() => sharePhoto(current)} aria-label="Share">
                            <ShareIcon />
                        </button>
                        <button
                            type="button"
                            className={`cpl-icon-btn ${confirmingDelete ? 'cpl-icon-btn--danger cpl-icon-btn--expanded' : ''}`}
                            onClick={handleDeleteClick}
                            aria-label={confirmingDelete ? 'Confirm delete' : 'Delete'}
                        >
                            <TrashIcon />
                            {confirmingDelete && <span className="cpl-icon-btn-label">Delete</span>}
                        </button>
                        <button type="button" className="cpl-icon-btn" onClick={onClose} aria-label="Close">
                            <CloseIcon />
                        </button>
                    </div>
                </header>
                <div
                    className="cpl-stage"
                    style={stageStyle}
                    onClick={handleStageClick}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchEnd}
                >
                    {index > 0 && (
                        <button type="button" className="cpl-nav cpl-nav--prev" onClick={prev} aria-label="Previous">
                            <ChevronLeft />
                        </button>
                    )}
                    <img src={current.url} alt="" className="cpl-image" draggable={false} />
                    {index < items.length - 1 && (
                        <button type="button" className="cpl-nav cpl-nav--next" onClick={next} aria-label="Next">
                            <ChevronRight />
                        </button>
                    )}
                </div>
                <div
                    className="cpl-footer"
                    style={{
                        position: 'fixed',
                        left: 12,
                        right: 12,
                        bottom: 12,
                        zIndex: 2147483647,
                        background: '#fff',
                        color: '#29282D',
                        borderRadius: 16,
                        padding: 14,
                        boxShadow: '0 20px 40px -18px rgba(0,0,0,0.5)',
                        maxHeight: '45vh',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                    }}
                >
                    <div className="cpl-actor-row">
                        <div className="cpl-actor-avatar" aria-hidden>
                            {(current.capturedByName || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div className="cpl-actor-meta">
                            <span className="cpl-actor-name">
                                {current.capturedByName === 'You'
                                    ? 'You added this'
                                    : `${current.capturedByName || 'Team member'} added this`}
                            </span>
                            <span className="cpl-actor-time">
                                {moment(current.capturedAt).fromNow()}
                                <span className="cpl-actor-dot" aria-hidden>·</span>
                                {moment(current.capturedAt).format('ddd D MMM YYYY')}
                            </span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 12, background: '#F7F7F9' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1E' }}>Can go on social media</div>
                            <div style={{ fontSize: 12.5, color: '#6E6E76', marginTop: 2 }}>
                                {shareSub}
                            </div>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={shareOk}
                            aria-label="Can go on social media"
                            onClick={toggleShare}
                            style={{ position: 'relative', flexShrink: 0, width: 44, height: 28, padding: 0, border: 'none', borderRadius: 999, background: shareOk ? '#29282D' : '#D8D8DE', cursor: 'pointer', transition: 'background 0.2s cubic-bezier(0.32, 0.72, 0, 1)' }}
                        >
                            <span style={{ position: 'absolute', top: 3, left: 3, width: 22, height: 22, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.2)', transform: shareOk ? 'translateX(16px)' : 'none', transition: 'transform 0.2s cubic-bezier(0.32, 0.72, 0, 1)' }} />
                        </button>
                    </div>

                    {current.bookingId && (
                        <div className="cpl-booking-card">
                            <div className="cpl-booking-service-row">
                                <svg className="cpl-booking-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="4" width="18" height="18" rx="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                                <span className="cpl-booking-service">
                                    {current.bookingService || 'Linked booking'}
                                </span>
                            </div>
                            <div className="cpl-booking-sub">
                                {current.bookingStaffName && <span>with {current.bookingStaffName}</span>}
                                {current.bookingStaffName && current.bookingDate && <span className="cpl-booking-sub-dot">·</span>}
                                {current.bookingDate && (
                                    <span>{moment(current.bookingDate).format('D MMM YYYY')}</span>
                                )}
                            </div>
                            {current.bookingNote && (
                                <div className="cpl-booking-note">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                    </svg>
                                    <span>{current.bookingNote}</span>
                                </div>
                            )}
                            {onViewBooking && (
                                <button type="button" className="cpl-view-booking" onClick={() => onViewBooking(current)}>
                                    View booking
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                        <polyline points="12 5 19 12 12 19" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return typeof document !== 'undefined' ? ReactDOM.createPortal(node, document.body) : node;
};

export default ClientPhotoLightbox;
